import React, { useEffect, useMemo, useState } from 'react';
import { CosmeticType, type PublicPlayerAppearance } from '@push.io/shared';
import { useAccountController, useAccountSnapshot } from '../../account/context.js';
import { DEV_FLAGS } from '../../dev/flags.js';
import { PlayerPreview } from '../customization/PlayerPreview.js';
import '../../ui/customization/customization.css';

const PRESETS: Array<{ label: string; appearance: PublicPlayerAppearance }> = [
  {
    label: 'Base',
    appearance: { baseColorId: 'cosm_base_blue', skinId: null, patternId: null, flagId: null, letterId: null, accessoryId: null, trailId: 'cosm_trail_basic', nameplateId: null, badgeId: null, victoryEffectId: null },
  },
  {
    label: 'Pattern + Trail',
    appearance: { baseColorId: 'cosm_base_red', skinId: null, patternId: 'cosm_pattern_stripes', flagId: null, letterId: null, accessoryId: null, trailId: 'cosm_trail_sparks', nameplateId: null, badgeId: null, victoryEffectId: null },
  },
  {
    label: 'Flag + Accessory',
    appearance: { baseColorId: 'cosm_base_white', skinId: null, patternId: null, flagId: 'cosm_flag_spain', letterId: null, accessoryId: 'cosm_acc_round_glasses', trailId: 'cosm_trail_dots', nameplateId: null, badgeId: null, victoryEffectId: null },
  },
  {
    label: 'Fallback asset',
    appearance: { baseColorId: 'cosm_base_green', skinId: null, patternId: 'unknown_pattern', flagId: null, letterId: null, accessoryId: 'unknown_accessory', trailId: 'cosm_trail_basic', nameplateId: null, badgeId: null, victoryEffectId: null },
  },
];

const TYPES = [
  CosmeticType.BaseColor,
  CosmeticType.Pattern,
  CosmeticType.Flag,
  CosmeticType.Accessory,
  CosmeticType.Trail,
] as const;

export function DevCosmeticsGallery() {
  const account = useAccountController();
  const snapshot = useAccountSnapshot();
  const [selected, setSelected] = useState<PublicPlayerAppearance>(PRESETS[0].appearance);
  const [quality, setQuality] = useState<'LOW' | 'MEDIUM' | 'HIGH'>('MEDIUM');

  useEffect(() => {
    void account.refreshAll();
  }, [account]);

  const catalogByType = useMemo(() => {
    const catalog = DEV_FLAGS.unlockAllCosmetics
      ? snapshot.catalog
      : snapshot.catalog.filter((item) => account.isOwned(item.id));
    return TYPES.map((type) => ({
      type,
      items: catalog.filter((item) => item.type === type),
    }));
  }, [account, snapshot.catalog]);

  return (
    <main className="customization-shell" style={{ minHeight: '100vh' }}>
      <header className="customization-header">
        <div className="customization-title">
          <strong>/dev/cosmetics</strong>
          <span>Renderer real de PixiJS con catálogo, presets, fallback y vista ampliada.</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(['LOW', 'MEDIUM', 'HIGH'] as const).map((level) => (
            <button key={level} className="customization-action secondary" onClick={() => setQuality(level)}>
              {level}{quality === level ? ' ✓' : ''}
            </button>
          ))}
        </div>
      </header>

      <div className="customization-body">
        <aside className="customization-preview-panel">
          <div className="customization-preview-stage" style={{ minHeight: 320 }}>
            <PlayerPreview appearance={selected} playerName="Preview" reducedMotion={quality === 'LOW'} />
          </div>
          <div className="customization-preview-stage" style={{ minHeight: 180 }}>
            <PlayerPreview appearance={selected} playerName="Partida" reducedMotion />
          </div>
          <div className="customization-preview-meta">
            <strong>Presets</strong>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {PRESETS.map((preset) => (
                <button key={preset.label} className="customization-action secondary" onClick={() => setSelected(preset.appearance)}>
                  {preset.label}
                </button>
              ))}
            </div>
            <p>Unlocked all cosmetics: {DEV_FLAGS.unlockAllCosmetics ? 'ON' : 'OFF'}. Quality: {quality}.</p>
          </div>
        </aside>

        <section className="customization-catalog-panel">
          {catalogByType.map(({ type, items }) => (
            <section key={type} style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 12 }}>{type}</h3>
              <div className="customization-grid" role="list">
                {items.map((item) => (
                  <button
                    key={item.id}
                    role="listitem"
                    className="cosmetic-card"
                    onClick={() => setSelected(applyCosmetic(selected, item.type, item.id))}
                  >
                    <div className="cosmetic-card-title">{item.slug}</div>
                    <span className="cosmetic-card-meta">{item.rarity}</span>
                    <span className="cosmetic-card-state">{account.isOwned(item.id) ? 'owned' : 'locked'}</span>
                  </button>
                ))}
                {items.length === 0 && <div className="customization-status">No items for {type}</div>}
              </div>
            </section>
          ))}
        </section>
      </div>
    </main>
  );
}

function applyCosmetic(appearance: PublicPlayerAppearance, type: CosmeticType, cosmeticId: string): PublicPlayerAppearance {
  const next = { ...appearance };
  switch (type) {
    case CosmeticType.BaseColor:
      next.baseColorId = cosmeticId;
      break;
    case CosmeticType.Pattern:
      next.patternId = cosmeticId;
      next.flagId = null;
      break;
    case CosmeticType.Flag:
      next.flagId = cosmeticId;
      next.patternId = null;
      break;
    case CosmeticType.Accessory:
      next.accessoryId = cosmeticId;
      break;
    case CosmeticType.Trail:
      next.trailId = cosmeticId;
      break;
    default:
      break;
  }
  return next;
}
